/**
 * Opus operators store — list + create + activate/deactivate.
 *
 * Same shape as aruba-users; the API path is the differentiator.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, patch as apiPatch, ApiError } from "@/api/client";
import {
  OpusUserListSchema,
  type OpusUser,
  type OpusUserCreate,
} from "@/api/schemas/opus-users";
import { MOCK_OPUS_USERS } from "@/api/fixtures/opus-users";

interface State {
  data: OpusUser[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  create: (payload: OpusUserCreate) => Promise<void>;
  setActive: (id: number, isActive: boolean) => Promise<void>;
  setAgentEnabled: (id: number, agentEnabled: boolean) => Promise<void>;
  /** Admin-only password reset. Generates a temp password server-side
   *  and returns it ONCE — caller must surface it to the admin (toast,
   *  modal) so they can pass it to the user. Returns the temp string. */
  resetPassword: (id: number) => Promise<string>;
}

export const useOpusUsers = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/users/");
      set({ data: OpusUserListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_OPUS_USERS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  create: async (payload) => {
    await apiPost("/users/", payload);
    await get().load();
  },

  setActive: async (id, isActive) => {
    set((s) => ({ data: s.data.map((u) => (u.id === id ? { ...u, isActive } : u)) }));
    try {
      await apiPatch(`/users/${id}/`, { isActive });
    } finally {
      await get().load();
    }
  },

  setAgentEnabled: async (id, agentEnabled) => {
    // Optimistic flip; the PATCH endpoint accepts agentEnabled
    // independently of isActive, so we don't disturb the active flag.
    set((s) => ({ data: s.data.map((u) => (u.id === id ? { ...u, agentEnabled } : u)) }));
    try {
      await apiPatch(`/users/${id}/`, { agentEnabled });
    } finally {
      await get().load();
    }
  },

  resetPassword: async (id) => {
    // Server generates the password and returns it in the response —
    // we intentionally do NOT store it. The caller is expected to show
    // it to the admin once (modal/toast) and let them copy it manually.
    const res = await apiPost<{ tempPassword: string }>(`/users/${id}/reset-password/`);
    return res.tempPassword;
  },
}));
